import { useEffect, useState } from 'react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { listMyTestimonials, updateTestimonialConsent } from './testimonialsApi';
import type { ConsentScope, Testimonial } from './types';

const SCOPE_LABEL: Record<ConsentScope, string> = {
  internal: 'Internal — instructor and program staff only',
  marketing: 'Marketing — anonymous quote in course/program materials',
  named_public: 'Named public — quoted with my first name and last initial',
};

const NARROWER: Record<ConsentScope, ConsentScope | null> = {
  named_public: 'marketing',
  marketing: 'internal',
  internal: null,
};

function StatusBadge({ status }: { status: Testimonial['status'] }) {
  const styles: Record<Testimonial['status'], string> = {
    submitted: 'bg-ws-surface-2 text-ws-ink-2',
    approved: 'bg-ws-success-quiet text-ws-success',
    withdrawn: 'bg-ws-surface-2 text-ws-ink-4 line-through',
  };
  return <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${styles[status]}`}>{status}</span>;
}

/** A student's own submitted testimonials + consent status. */
export default function MyTestimonials() {
  const [items, setItems] = useState<Testimonial[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    listMyTestimonials().then(setItems).catch(() => setError('Could not load your testimonials.'));
  };
  useEffect(load, []);

  const narrow = async (t: Testimonial) => {
    const next = NARROWER[t.consent_scope];
    if (!next) return;
    setBusyId(t.id);
    try {
      await updateTestimonialConsent(t.id, { consent_scope: next, display_name_ok: false });
      load();
    } catch {
      setError('Could not update consent. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (t: Testimonial) => {
    setBusyId(t.id);
    try {
      await updateTestimonialConsent(t.id, { revoke: true });
      load();
    } catch {
      setError('Could not revoke consent. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  if (items === null) return <Spinner label="Loading your testimonials…" />;

  if (items.length === 0) {
    return (
      <Card className="p-6 text-center text-sm text-ws-ink-3">
        You haven’t submitted a testimonial yet.
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {error && <p className="text-sm text-ws-danger">{error}</p>}
      {items.map((t) => (
        <Card key={t.id} className="p-4">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm text-ws-ink">{t.body}</p>
            <StatusBadge status={t.status} />
          </div>
          <p className="mt-2 text-xs text-ws-ink-3">{SCOPE_LABEL[t.consent_scope]}</p>
          {t.status !== 'withdrawn' && (
            <div className="mt-3 flex gap-2">
              {NARROWER[t.consent_scope] && (
                <Button size="sm" variant="secondary" disabled={busyId === t.id} onClick={() => narrow(t)}>
                  Narrow to {NARROWER[t.consent_scope]}
                </Button>
              )}
              <Button size="sm" variant="danger" disabled={busyId === t.id} onClick={() => revoke(t)}>
                Withdraw
              </Button>
            </div>
          )}
          {t.status === 'withdrawn' && t.consent_revoked_at && (
            <p className="mt-2 text-xs text-ws-ink-4">Withdrawn {new Date(t.consent_revoked_at).toLocaleDateString()}.</p>
          )}
        </Card>
      ))}
    </div>
  );
}
